/**
 * "Made by" — who or what made a task (Tasks Both Ways slice 5, 10/28, pick
 * 2A — https://claude.ai/artifact/H7gsi8ERjULUpGCpUMZzH8).
 *
 * Automatic tasks live on Mine with a gray, dashed "Made by" line — no person
 * asked, so nobody waits for a reply — and a From people / From the system
 * filter. The server sends `madeBy` (services/taskSources.ts); null means a
 * person asked, and the card already says who.
 */
import {
    AlertTriangle,
    Calendar,
    ClipboardList,
    FileText,
    Gauge,
    Mail,
    MapPin,
    Package,
    Phone,
    Search,
    Sparkles,
    User,
    Wallet,
    Wrench,
    type LucideIcon,
} from "lucide-react";

export interface MadeBy {
    kind: "person" | "system" | "claude";
    name: string;
    icon: string;
    personName: string | null;
}

export const MADE_BY_ICON: Record<string, LucideIcon> = {
    package: Package,
    mail: Mail,
    gauge: Gauge,
    phone: Phone,
    wrench: Wrench,
    calendar: Calendar,
    map: MapPin,
    money: Wallet,
    clipboard: ClipboardList,
    alert: AlertTriangle,
    user: User,
    sparkles: Sparkles,
    search: Search,
    file: FileText,
};

/** "Made by: Can't find it · Tristian Lee", or "Claude drafted · Corbin Moyer sent". Pure. */
export function madeByLine(m: MadeBy): string {
    if (m.kind === "claude") return `${m.name}${m.personName ? ` · ${m.personName}` : ""}`;
    return `Made by: ${m.name}${m.personName ? ` · ${m.personName}` : ""}`;
}

export type FromFilter = "all" | "people" | "system";

export const FROM_FILTERS: Array<{ id: FromFilter; label: string }> = [
    { id: "all", label: "All" },
    { id: "people", label: "From people" },
    { id: "system", label: "From the system" },
];

/**
 * Made by the system, rather than asked by a person? A Claude draft is from a
 * person: Corbin checked it and sent it (slice 6).
 */
export function isFromSystem(t: { madeBy?: MadeBy | null }): boolean {
    return t.madeBy?.kind === "system";
}

export function passesFrom(t: { madeBy?: MadeBy | null }, f: FromFilter): boolean {
    if (f === "all") return true;
    return f === "system" ? isFromSystem(t) : !isFromSystem(t);
}
